/* eslint-disable react/prop-types */
import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Form, Search, Icon, Message } from "semantic-ui-react";
import { observer } from "mobx-react";
import cx from "classnames";
import uuidv1 from "uuid/v1";
import Country from "~/models/Country";
import ExpandPanel from "~/components/element/ExpandPanel/ExpandPanel";
import AddressModel from "~/models/Address";
import Geocoder from "~/utils/GoogleMaps/Geocoder";
import GeocoderResultDecorator from "~/utils/GoogleMaps/GeocoderResultDecorator";
import ValidateSchema, { getValidationErrors } from "~/utils/ReactReformed/ValidateSchema";
import "./Address.less";

// Use this schema along with utils/ValidateSchema
const addressValidation = {
  zip: {
    address: "zip",
  },
  country: {
    address: "country",
    required: true,
  },
  state: {
    address: "state",
  },
  city: {
    address: "city",
  },
  street: {
    required: true,
  },
};

@observer
class Address extends Component {
  formId = null;

  constructor(props) {
    super(props);

    this.onInlineAddressChange = this.onInlineAddressChange.bind(this);
    this.onDetailedAddressChange = this.onDetailedAddressChange.bind(this);
    this.geocoder = new Geocoder();
    // Generate a random hash of 4 chars long. This will help us creating unique input html id
    // that we'll use to bind to their corresponding labels, so when the user clicks the label
    // the focus is set on the input. The random hash is needed because this Address component
    // can be rendered more than once, and without the hash we'll end up with duplicated html node ids.
    this.formId = uuidv1()
      // uuid create groups separated by -, let's remove them.
      .replace(/-/g, "")
      .substring(0, 4);

    this.state = {
      showDetails: false,
      loadingResults: false,
    };
  }

  /**
   * This might not be needed if countries and country are both set at the same time.
   * @todo: check eventually.
   */
  componentDidUpdate(prevProps) {
    // force re-rendering for validation purpose. Country validation requires a list of countries.
    const { countries } = this.props;

    // countries have been set.
    if (prevProps.countries.length === 0 && countries.length > 0) {
      this.forceUpdate();
    }
  }

  onInlineAddressChange(value) {
    const { setModel, model, minSearchLength, setAddressResults, setSelectedAddressResult } = this.props;

    setModel({ ...model, inline: value });

    setAddressResults([]);
    setSelectedAddressResult(null);

    // Do not trigger a search if not enough chars.
    if (!(value && value.length >= minSearchLength)) {
      return;
    }

    this.setState({ loadingResults: true });

    this.geocoder.geocode({ address: value }, addressResults => {
      this.setState({ loadingResults: false });
      setAddressResults(addressResults);
    });
  }

  onDetailedAddressChange(property, value) {
    const { setModel, model, setAddressResults, setSelectedAddressResult } = this.props;

    let newModel = setModel({ ...model, [property]: value });

    setSelectedAddressResult(null);

    // when the country changes, clear out the state value (if any)
    if (property === "country") {
      newModel = setModel({ ...newModel, state: "" });
    }

    const addressModel = new AddressModel(newModel);
    this.setState({ loadingResults: true });

    this.geocoder.geocode({ address: addressModel.getMappableAddress() }, addressResults => {
      setAddressResults(addressResults);
      setSelectedAddressResult(addressResults.length > 0 ? addressResults[0] : null);
      this.setState({ loadingResults: false });
    });
  }

  onResultSelect(result) {
    const { setModel, model, addressResults, setSelectedAddressResult, onLatLngChange } = this.props;

    const selectedResult = addressResults.find(addressResult => addressResult.formatted_address === result.title);

    if (!selectedResult) {
      throw new Error("Could not find selected result");
    }

    const decorator = new GeocoderResultDecorator(selectedResult);

    const newModel = {
      ...model,
      inline: selectedResult.formatted_address,
      street: decorator.fullStreet,
      street2: decorator.street2,
      city: decorator.city,
      country: decorator.country,
      state: decorator.state,
      zip: decorator.zip,
    };

    setModel(newModel);

    setSelectedAddressResult(selectedResult);

    if (onLatLngChange && selectedResult.geometry && selectedResult.geometry.location) {
      const {
        geometry: { location },
      } = selectedResult;

      const lat = typeof location.lat === "function" ? location.lat() : location.lat;
      const lng = typeof location.lng === "function" ? location.lng() : location.lng;

      onLatLngChange({ lat, lng });
    }

    // validate locally in order to tell if there are missing fields for the given selected address.
    const inflightSchema = getValidationErrors(addressValidation, newModel, this.props);

    if (this.getMissingFields(inflightSchema).length > 0) {
      this.setState({ showDetails: true });
    }
  }

  getHtmlIdFor(baseId) {
    return `${baseId}-${this.formId}`;
  }

  getMissingFields(schema) {
    const fields = ["street", "city", "state", "country", "zip"];
    const missingFields = [];

    fields.forEach(field => {
      if (schema.fields[field] && !schema.fields[field].isValid) {
        missingFields.push(field);
      }
    });

    return missingFields;
  }

  toggleDetails() {
    const { showDetails } = this.state;

    const newShowDetails = !showDetails;
    this.setState({ showDetails: newShowDetails });
  }

  isFormValid() {
    const { schema } = this.props;

    return (
      schema.fields.street &&
      schema.fields.street.isValid &&
      schema.fields.city &&
      schema.fields.city.isValid &&
      schema.fields.state &&
      schema.fields.state.isValid &&
      schema.fields.country &&
      schema.fields.country.isValid &&
      schema.fields.zip &&
      schema.fields.zip.isValid
    );
  }

  render() {
    const {
      model,
      isDirty,
      schema,
      countries,
      hasSelectedResult,
      minSearchLength,
      addressResults,
      bindInput,
      t,
      useTranslation,
      serverValidationErrors,
    } = this.props;
    const { showDetails, loadingResults } = this.state;
    let states = [];
    let selectedCountry = null;

    if (model.country) {
      selectedCountry = countries.find(countryInLoop => countryInLoop.id === model.country);

      if (selectedCountry) {
        states = selectedCountry.states;
      }
    }

    let postalCodeErrorMessage = "";

    if (selectedCountry && selectedCountry.zipCodeErrorMessage) {
      if (useTranslation) {
        postalCodeErrorMessage = t(`address:postalCodeErrorMessageFor${selectedCountry.title.replace(/ /g, "")}`);
      } else {
        postalCodeErrorMessage = selectedCountry.zipCodeErrorMessage;
      }
    }

    const hasServerErrors = Object.keys(serverValidationErrors).length > 0;
    const validAddress = this.isFormValid() && hasSelectedResult;
    const missingFields = this.getMissingFields(schema);

    return (
      <div className="address-wizard">
        <Form.Input
          fluid
          loading={loadingResults}
          showNoResults={typeof model.inline !== "undefined" && model.inline.length >= minSearchLength}
          noResultsMessage={
            <span>
              No results found.&nbsp;
              <a href="#" onClick={() => this.toggleDetails()}>
                Enter manually
              </a>
            </span>
          }
          control={Search}
          id={this.getHtmlIdFor("tour-address-inline")}
          label={useTranslation ? t("clientPanelInfo:enterAnAddress") : "* Enter an address"}
          placeholder={useTranslation ? t("clientPanelInfo:enterFullPartialAddress") : "Enter full or partial address"}
          name="inline"
          value={model.inline || ""}
          results={addressResults.map(result => ({ title: result.formatted_address }))}
          onSearchChange={event => this.onInlineAddressChange(event.target.value)}
          onResultSelect={(_event, data) => this.onResultSelect(data.result)}
          disabled={showDetails}
        />
        <Message
          size="small"
          warning={!validAddress}
          positive={validAddress}
          className={cx("address-status", {
            // This should display as long as the form has valid input (which doesn't mean it is a valid address),
            // and will show only until the user has triggered a search.
            show: addressResults.length > 0 || ((model.inline || "").length > 0 && isDirty),
          })}
        >
          {/* This will tell if the given address is a valid Geo location address */}
          <Icon name={validAddress ? "check" : "warning"} />
          {validAddress ? "valid" : "invalid"} address.
          {missingFields.length > 0 ? ` Please complete the following fields: ${missingFields.join(", ")}.` : ""}
        </Message>
        <ExpandPanel
          className="details"
          open={showDetails || hasServerErrors}
          onClick={() => this.toggleDetails()}
          label={useTranslation ? t("clientPanelInfo:addressDetails") : "Address details"}
        >
          <Form.Input
            {...bindInput("street", schema, serverValidationErrors)}
            fluid
            label={useTranslation ? t("clientPanelInfo:addressLine1") : "Address Line 1"}
            id={this.getHtmlIdFor("street")}
            placeholder={useTranslation ? t("clientPanelInfo:addressLine1") : "Address Line 1"}
            onChange={event => this.onDetailedAddressChange(event.target.name, event.target.value)}
          />
          <Form.Input
            {...bindInput("street2", schema, serverValidationErrors)}
            fluid
            id={this.getHtmlIdFor("street2")}
            label={useTranslation ? t("clientPanelInfo:addressLine2") : "Address Line 2"}
            placeholder={useTranslation ? t("clientPanelInfo:addressLine2") : "Address Line 2"}
            onChange={event => this.onDetailedAddressChange(event.target.name, event.target.value)}
          />
          <Form.Input
            {...bindInput("city", schema, serverValidationErrors)}
            fluid
            id={this.getHtmlIdFor("city")}
            label={useTranslation ? t("clientPanelInfo:asteriskCity") : "* City"}
            placeholder={useTranslation ? t("clientPanelInfo:city") : "City"}
            onChange={event => this.onDetailedAddressChange(event.target.name, event.target.value)}
          />
          <Form.Group widths="equal">
            <Form.Select
              {...bindInput("country", schema, serverValidationErrors)}
              fluid
              id={this.getHtmlIdFor("country")}
              label={useTranslation ? t("clientPanelInfo:asteriskCountry") : "* Country"}
              loading={countries.length === 0}
              placeholder={useTranslation ? t("clientPanelInfo:country") : "Country"}
              options={countries.map(countryInLoop => ({
                key: countryInLoop.id,
                text: countryInLoop.id,
                value: countryInLoop.id,
              }))}
              onChange={(_event, data) => this.onDetailedAddressChange("country", data.value)}
            />
            {!selectedCountry || selectedCountry.hasStates ? (
              <Form.Select
                {...bindInput("state", schema, serverValidationErrors)}
                fluid
                disabled={!selectedCountry}
                id={this.getHtmlIdFor("state")}
                label={useTranslation ? t("clientPanelInfo:asteriskState") : "* State"}
                placeholder={useTranslation ? t("clientPanelInfo:state") : "State"}
                options={Object.keys(states).map(key => ({ key, text: states[key], value: key }))}
                onChange={(_event, data) => this.onDetailedAddressChange("state", data.value)}
              />
            ) : null}
            <Form.Input
              {...bindInput("zip", schema, serverValidationErrors)}
              fluid
              id={this.getHtmlIdFor("zip")}
              label={useTranslation ? t("clientPanelInfo:asteriskZip") : "* Zip"}
              placeholder={useTranslation ? t("clientPanelInfo:zip") : "Zip"}
              onChange={event => this.onDetailedAddressChange(event.target.name, event.target.value)}
            />
          </Form.Group>
          <div className="help">{postalCodeErrorMessage}</div>
        </ExpandPanel>
      </div>
    );
  }
}

const modelPropTypes = {
  inline: PropTypes.string,
  street: PropTypes.string,
  street2: PropTypes.string,
  city: PropTypes.string,
  country: PropTypes.string,
  state: PropTypes.string,
  zip: PropTypes.string,
};

const schemaPropTypes = {
  street: PropTypes.object,
  city: PropTypes.object,
  state: PropTypes.object,
  country: PropTypes.object,
  zip: PropTypes.object,
};

Address.propTypes = {
  // specific to react-reformed
  model: PropTypes.shape({ ...modelPropTypes }).isRequired,
  schema: PropTypes.shape({
    isValid: PropTypes.bool,
    fields: PropTypes.shape({ ...schemaPropTypes }).isRequired,
  }).isRequired,
  isDirty: PropTypes.bool,
  setModel: PropTypes.func.isRequired,
  bindInput: PropTypes.func.isRequired,
  // everything else.
  // countries should be provided by CountryStore or something similar.
  countries: PropTypes.arrayOf(PropTypes.instanceOf(Country)),
  minSearchLength: PropTypes.number,
  addressResults: PropTypes.array.isRequired,
  hasSelectedResult: PropTypes.bool,
  setAddressResults: PropTypes.func.isRequired,
  setSelectedAddressResult: PropTypes.func.isRequired,
  // Note the lat/lng can be accessed via the selected address result's geometry.location,
  // this function is just a handy way of accessing those values if the only important prop of selectedAddressResult is
  // its lat/lng.
  onLatLngChange: PropTypes.func,
  t: PropTypes.func.isRequired,
  // @todo Remove when we will find another solution for the translation at the provider panel
  useTranslation: PropTypes.bool,
  serverValidationErrors: PropTypes.shape({
    street: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    country: PropTypes.string,
    zip: PropTypes.string,
  }),
};

Address.defaultProps = {
  minSearchLength: 5,
  hasSelectedResult: false,
  useTranslation: false,
  serverValidationErrors: {},
};

export default withTranslation()(Address);

// Handy component already with validation bindings, in other words the prop "schema" will
// be passed automatically.
const AddressWithValidation = withTranslation()(ValidateSchema({ ...addressValidation })(Address));

export { modelPropTypes, schemaPropTypes, addressValidation, AddressWithValidation };
